import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  embedEnding,
  embedListRoute,
  embedListStep,
  embedSubtitlePref,
  formatRoute,
  parseRoute,
  type EmbedEnding,
  type EmbedList,
  type EmbedOptions,
  type UiLocaleId,
} from '@visious/core';
import { navigate, navigateTo } from '../../router/router';
import { readSettings } from '../../platform/settings';
import { activeLocale, applyUiLocaleOverride } from '../../strings';
import { useCopy } from '../../strings/useCopy';
import { useWatchScreen, type WatchBinding } from '../watch/useWatchScreen';
import { useEmbedList, type EmbedListRead } from './useEmbedList';

/**
 * The /embed screen's model (R13 — the view renders props): the watch pipeline, plus everything the
 * embed URL itself asked for.
 *
 * The parameters arrive as {@link EmbedOptions} on the route, already read and validated by core, so
 * what is left here is *acting* on them. They split three ways, and the split is what this file is:
 *
 * - **The session's own defaults** — `autoplay`, `mute`, `cc_load_policy`/`cc_lang_pref`,
 *   `disablekb` — handed to `useWatchScreen`, which reads each once at mount like the stored
 *   preference it stands in for. None of them is written back: an embedder speaks for the page the
 *   video is on, never for the reader's settings.
 * - **What the bar is allowed to be** — `controls`, `fs`, `color` — returned for the view to hand to
 *   `PlayerSurface`.
 * - **When the video is over and what happens then** — `end`, `loop`, `playlist`, which are one
 *   question rather than three ({@link useEmbedEnding}).
 *
 * `hl` is the odd one out: it is not about this player at all but about the words around it, so it
 * goes through the copy catalogue's own override.
 */

/**
 * What the address bar said, as the screen receives it.
 *
 * `videoId` is `null` for the one embed address that names a list and no video
 * (`/embed/?list=PL…`), which `App.tsx` sends to its own component: that one has to ask the
 * instance which video it is showing before there is a player to build.
 */
export interface EmbedTarget {
  readonly videoId: string | null;
  readonly startSec: number | null;
  readonly options: EmbedOptions;
  readonly list: EmbedList | null;
}

/** The same target, once the video is known — what {@link useEmbedScreen} takes. */
export interface EmbedVideoTarget extends EmbedTarget {
  readonly videoId: string;
}

/** The other half: an address naming a list, which is what the landing screen resolves. */
export interface EmbedListTarget extends EmbedTarget {
  readonly list: EmbedList;
}

export interface EmbedBinding {
  readonly binding: WatchBinding;
  /** `controls=0` draws no bar at all. The picture keeps its tap gesture and its big play disc. */
  readonly bar: boolean;
  /** `fs=0` takes the bar's fullscreen button, and only that. */
  readonly fullscreenButton: boolean;
  /** `color=white` paints the played part of the bar white instead of the player's own red. */
  readonly whiteProgress: boolean;
  /**
   * Where the two step buttons in the top band go, or `null` on the side the list has no entry.
   * The whole pair is `null` where there is no list, and the band then draws neither.
   */
  readonly step: EmbedStep | null;
  /** Where the band's title link goes — the real watch page, carrying the list where there is one. */
  readonly watchHref: string;
}

export interface EmbedStep {
  readonly previousHref: string | null;
  readonly nextHref: string | null;
}

/**
 * The /embed screen's one route decision (R13 — the view builds no URLs): where the title link
 * leads. The watch page for the same video, bare — no `t`, because the reader is mid-playback and
 * the link means "open the full app around this video", not "start over where the embed URL said".
 *
 * **A list travels with it.** The reader following this link is leaving the iframe for the real
 * page, and arriving there without the playlist would leave them with the one video and no way back
 * to the rest of it.
 */
export function embedWatchHref(videoId: string, list: EmbedList | null): string {
  return formatRoute({
    kind: 'watch',
    videoId,
    startSec: null,
    playlistId: list?.playlistId ?? null,
    index: list?.index ?? null,
  });
}

/** The address this embed is at, which is the whole input to {@link useEmbedEnding}'s decision. */
function embedHref(target: EmbedTarget): string {
  return formatRoute({
    kind: 'embed',
    videoId: target.videoId,
    startSec: target.startSec,
    options: target.options,
    list: target.list,
  });
}

/**
 * What happens when the video is over, read off an address rather than off an object — so the memo
 * below has something stable to key on (`useScreen` re-derives the screen on every render, so the
 * options object is a new one each time). `formatRoute`/`parseRoute` are a tested fixed point over
 * every embed arm, so nothing is lost on the way round.
 */
function endingAt(href: string): EmbedEnding {
  const route = parseRoute(href);
  return route.kind === 'embed' ? embedEnding(route) : { kind: 'stop' };
}

/**
 * `hl` — the language this *page* speaks, whatever the reader's setting says.
 *
 * A layout effect rather than an ordinary one so the switch lands before the first paint: the
 * loading line is already on screen by the time an effect would run, and an embed that says
 * "Loading video…" for a frame and then "Video wird geladen…" has told the reader twice. Leaving the
 * page hands the setting its say back, which is what an override that outlives its screen would take
 * away — the same reader is one navigation from a `/watch` page in their own language.
 */
function useUiLanguage(wanted: UiLocaleId | null): void {
  useLayoutEffect(() => {
    applyUiLocaleOverride(wanted, readSettings().uiLocale);
    return () => applyUiLocaleOverride(null, readSettings().uiLocale);
  }, [wanted]);
}

/**
 * Where the two step buttons in the band lead — and, when a video ends inside a list, where the
 * screen goes next. One builder for both, so a press and an ending write the same address.
 *
 * The id is handed over **where the page in hand has it** and left `null` where it does not, which
 * is core's own seam: an entry named by its position resolves on arrival. That is what makes a step
 * across a page boundary work without reading a second page first, and it is why nothing here waits
 * for a fetch before the buttons come alive.
 */
function embedStepHref(
  target: EmbedTarget,
  read: EmbedListRead,
  direction: 'next' | 'previous',
): string | null {
  const list = target.list;
  if (list === null) return null;
  const index = embedListStep(list, {
    total: read.total,
    loop: target.options.loop,
    direction,
  });
  if (index === null) return null;
  const route = {
    kind: 'embed',
    videoId: target.videoId,
    startSec: target.startSec,
    options: target.options,
    list,
  } as const;
  return formatRoute(embedListRoute(route, { index, videoId: read.entries.get(index) ?? null }));
}

/**
 * `end`, `loop` and `playlist` as one question — "the video is over, now what" — because they answer
 * it together and separating them gets `?loop=1&end=30` wrong: a range that is looped has to start
 * again at the mark, not stop there.
 *
 * **Over** is either the video actually ending or the playhead reaching `end`. Core decides what
 * follows (`embedEnding`): stop where it is, start again at `start`, or navigate to the next entry —
 * navigating, so the address always says what is playing and a reload lands mid-sequence rather than
 * back at the beginning.
 *
 * The mark fires **once per run**, and a seek back before it arms it again. Without that the pause
 * would land on every poll for as long as the playhead sat past the mark, so a reader pressing Play
 * after an `end=` would get a video that refused to move.
 */
function useEmbedEnding(target: EmbedTarget, binding: WatchBinding, nextHref: string | null): void {
  const { handlers, stage, transport } = binding;
  const endSec = target.options.endSec;
  const startSec = target.startSec ?? 0;
  const href = embedHref(target);
  const ending = useMemo(() => endingAt(href), [href]);
  const armed = useRef(true);
  // Only the real player has a transport to read or a command to take: the no-MSE arm is a plain
  // `<video>` with the browser's own controls, and its position never leaves zero.
  const hasPlayer = stage.kind === 'player';

  useEffect(() => {
    if (!hasPlayer) return;
    const over = transport.ended || (endSec !== null && transport.currentSec >= endSec);
    if (!over) {
      armed.current = true;
      return;
    }
    if (!armed.current) return;
    armed.current = false;

    switch (ending.kind) {
      case 'stop':
        // Not a toggle: the video has stopped itself at its own end in every case but `end=`, and a
        // toggle there would start a paused video rather than stop a playing one.
        handlers.pause();
        return;
      case 'replay':
        // Back to `start`, not to zero: `?start=30&end=60&loop=1` asks for those thirty seconds
        // over and over, and the second lap has to be the same thirty as the first.
        handlers.command({ kind: 'seek-to', seconds: startSec });
        handlers.command({ kind: 'play' });
        return;
      case 'next':
        navigateTo(ending.route);
        return;
      case 'list': {
        /*
         * The list answers it, and the answer needs the playlist's length — which is why this arm is
         * here and not in core. The end of a list is an ending: the player stops on the last frame
         * rather than the run starting over, unless `loop=1` asked for exactly that (the wrap is
         * `embedListStep`'s, so one rule covers the button and the ending alike).
         */
        if (nextHref === null) handlers.pause();
        else navigate(nextHref);
        return;
      }
    }
    // `nextHref` rather than the list itself, so this closes over a **string**: the screen is
    // re-derived on every render, and an object dependency would re-run the effect four times a
    // second with the transport poll.
  }, [ending, endSec, handlers, hasPlayer, nextHref, startSec, transport.currentSec, transport.ended]);
}

/**
 * Which entry a list embed lands on, given the page in hand.
 *
 * The address asks for an index and the playlist answers with a page, and the two can disagree: a
 * playlist loses entries, somebody edits the number by hand, and the endpoint answers a page past
 * the end with a header and no rows at `200` rather than with an error. So the exact entry is
 * preferred, then the first one after it on the page, and then whatever the page does have — a list
 * with videos in it always plays one of them.
 */
function embedLanding(read: EmbedListRead, wanted: number | null): number | null {
  if (read.entries.size === 0) return null;
  if (wanted === null) return read.firstIndex;
  if (read.entries.has(wanted)) return wanted;
  const after = [...read.entries.keys()].filter((index) => index > wanted);
  return after.length === 0 ? read.firstIndex : Math.min(...after);
}

/** What the landing screen draws while it works out which video the address is about. */
export interface EmbedLanding {
  readonly loading: boolean;
  /** One sentence, or `null` — the playlist did not load, or has nothing in it. */
  readonly problem: string | null;
  /** Where "Open this playlist" goes: the real page, in a tab of its own. */
  readonly playlistHref: string;
}

/**
 * `/embed/?list=PL…` — the one embed address that cannot be read without asking the instance
 * something.
 *
 * Which video a list starts at is the playlist's answer to give, so this screen has no player at
 * all: it reads the page the wanted entry is on, then **replaces** the address with the entry it
 * landed on. Replaces rather than pushes, because the reader never chose this address as a step of
 * its own — it is the one the embedder wrote — and a Back press should leave the run rather than
 * walk back into a resolve that would immediately resolve again.
 *
 * An index past the end of the list is answered once, by asking for the list's own beginning. That
 * cannot loop: the second address names no entry, and a page 1 with no rows is an empty playlist
 * rather than a page out of range.
 */
export function useEmbedLanding(target: EmbedListTarget): EmbedLanding {
  const words = useCopy().text.watch.page;
  const list = target.list;
  const read = useEmbedList(list);
  const index = embedLanding(read, list.index);
  const videoId = index === null ? null : (read.entries.get(index) ?? null);

  useEffect(() => {
    if (read.loading || read.problem !== null) return;
    if (videoId === null || index === null) {
      // Nothing at that position. An index that was asked for is retried at the list's start; an
      // address that asked for no entry has already been answered, and the list is empty.
      if (list.index !== null) {
        navigateTo(
          { kind: 'embed', videoId: null, startSec: target.startSec, options: target.options, list: { playlistId: list.playlistId, index: null } },
          { replace: true },
        );
      }
      return;
    }
    navigateTo(
      { kind: 'embed', videoId, startSec: target.startSec, options: target.options, list: { playlistId: list.playlistId, index } },
      { replace: true },
    );
  }, [index, list, read.loading, read.problem, target.options, target.startSec, videoId]);

  // Still working: the page is on the wire, or it has arrived and the effect above is one render
  // from replacing the address — with the entry it found, or with a second ask at the list's start.
  const settling = videoId !== null || list.index !== null;
  const loading = read.loading || (read.problem === null && settling);
  return {
    loading,
    problem: read.problem ?? (loading ? null : words.listEmpty),
    playlistHref: formatRoute({ kind: 'playlist', playlistId: list.playlistId }),
  };
}

export function useEmbedScreen(target: EmbedVideoTarget): EmbedBinding {
  const options = target.options;
  useUiLanguage(options.uiLanguage);

  /*
   * The page of the playlist this entry is on — the two step buttons and the advance, and nothing
   * else. It is not the queue: `playlist: false` still holds below, because a queue is a list this
   * *tab* is watching through and an embed is a player in somebody else's page.
   */
  const list = useEmbedList(target.list);

  /*
   * Read once, like every other playback default this screen stands in for — the menu keeps the last
   * word from there on. The player's own language is the fallback for `cc_load_policy=1` with no
   * language named, and it is `hl` where the address gave one: the override above has not been
   * applied yet on this first render, and the language the page is *about* to speak is the honest
   * answer to "captions this reader can read".
   */
  const [subtitle] = useState(() =>
    embedSubtitlePref(
      options,
      readSettings().playback.subtitle,
      options.uiLanguage ?? activeLocale().ui,
    ),
  );

  /*
   * Where the two step buttons lead, built once: the ending effect takes the forward one as a
   * string, so what it closes over is stable across the transport's four-times-a-second render.
   */
  const previousHref = embedStepHref(target, list, 'previous');
  const nextHref = embedStepHref(target, list, 'next');

  const binding = useWatchScreen(
    /*
     * The list goes in even though no panel and no queue are built from it (`playlist: false` below
     * stands both of those down). Two things downstream read it and would otherwise be wrong about
     * this page: the Watch on links carry the playlist out with them, so a reader leaving for
     * YouTube or PeerTube leaves with the list they were watching rather than with one video out of
     * it, and the history mirror records the entry as part of the list it was played from.
     */
    {
      videoId: target.videoId,
      startSec: target.startSec,
      playlistId: target.list?.playlistId ?? null,
      index: target.list?.index ?? null,
    },
    {
      // The three that are about being embedded at all rather than about this URL. No comment
      // section is rendered, so none may be fetched. The Watch on links open a tab of their own,
      // because following one in place would put the destination inside the embedder's frame and
      // take the page the reader was on with it. And no playlist panel and no queue: an embed route
      // can carry a `list` since 2026-09-18, but a side panel in a 560×315 box is furniture, and a
      // queue is the list *this tab* is watching through — an embed must not edit one. What the
      // list is for here is the two step buttons and the advance (`useEmbedList`), both of which
      // move by navigating, which is the same mechanism the `playlist=` sequence uses.
      comments: false,
      jumpOutNewTab: true,
      playlist: false,
      /*
       * Always passed, never left to the setting. "Start playing on arrival" is a statement about
       * pages the reader opened; an embed is a video somebody else put on a page of their own, and
       * an absent `autoplay=1` there is a decision rather than a gap. So this changes what an embed
       * does for a reader who has that setting on: it now waits to be asked.
       */
      autoplay: options.autoplay,
      startMuted: options.mute,
      subtitle,
      keyboard: options.keyboard,
    },
  );

  useEmbedEnding(target, binding, nextHref);

  return {
    binding,
    bar: options.controls,
    fullscreenButton: options.fullscreen,
    whiteProgress: options.progressColor === 'white',
    /*
     * `controls=0` takes the step buttons along with the bar, even though they ride in the band
     * rather than on the picture: the embedder asked for a picture with no transport on it, and a
     * step through the list is transport. The band keeps its title and its two library buttons
     * there, which is what it is for — the decision is `docs/specs/urls.md`'s, with the parameter
     * it belongs to.
     */
    step: target.list === null || !options.controls ? null : { previousHref, nextHref },
    watchHref: embedWatchHref(target.videoId, target.list),
  };
}
